import Add16 from '@carbon/icons-react/es/add/16';
import Edit16 from '@carbon/icons-react/es/edit/16';
import Menu16 from '@carbon/icons-react/es/menu/16';
import { Dropdown, Tooltip, Typography } from 'antd';
import React, { FC, useCallback, useMemo, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled, { createGlobalStyle } from 'styled-components';
import { ProjectBaseline, ProjectBaselineUpdateRequest } from 'api/project';
import { ReactComponent as NoteSvg } from 'assets/svgs/note.svg';
import { Button } from 'components/atoms/button';
import { Checkbox } from 'components/atoms/checkbox';
import { Menu } from 'components/atoms/dropdown';
import { toastService } from 'components/molecules/toast';
import {
  useActiveBaselinesByProjectId,
  useProjectBaselineByProjectId,
} from 'data/projects/hooks/use-project-baseline';
import { useTranslate } from 'hooks/use-translation';
import {
  useCreateProjectBaselineMutation,
  useDeleteProjectBaselineMutation,
  useUpdateProjectBaselineMutation,
} from 'queries/project-baseline-mutation';
import { BaselineAddModal } from './add';
import { BaselineEditModal } from './edit';
import { BaselineFormProps } from './form';
import { useBaselineRequestPayload } from './use-request-payload';

const SetBaselineHeader = styled(Menu.Header)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  pointer-events: auto;
`;

const AddButton = styled(Button)`
  padding-right: 0;
  padding-bottom: 0;
  padding-top: 0;
`;

const BaselineMenuItem = styled(Menu.Item)`
  display: flex;
  align-items: center;
  height: 32px;
  line-height: 32px;

  &.ant-menu-item-selected {
    background: transparent;
  }

  &:hover {
    background-color: var(--item-hover-bg);
  }

  .edit-icon {
    visibility: hidden;
    color: #000;
    margin-right: 0;
  }

  &:hover {
    .edit-icon {
      visibility: visible;
    }
  }
`;

const StyledCheckbox = styled(Checkbox)`
  height: auto;
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 0;

  .ant-checkbox + span {
    display: flex;
    align-items: center;
    flex: 1;
    min-width: 0;
  }
`;

const BaselineName = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  min-width: 0;

  span:first-child {
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const NoteIcon = styled.span`
  margin-left: var(--spacing-03);
  display: flex;

  svg path {
    fill: var(--orange-40);
  }
`;

const TooltipNote = styled.pre`
  font-family: inherit;
  font-size: inherit;
  line-height: inherit;
  margin: 0;
  white-space: initial;
`;

const BaselineMenu = styled(Menu)`
  border: none;
  padding: var(--spacing-03) 0;
`;

const MenuWrapper = styled.div`
  box-shadow: var(--shadow-08);
`;

const DisabledTooltip = styled(Tooltip)`
  display: flex;
  align-items: center;
  width: 100%;
`;

const EmptyState = styled.div`
  line-height: initial;
  margin-top: var(--spacing-05);
  padding: var(--spacing-05);
  color: var(--gray-70);
  font-size: var(--font-size-sm);
  padding-bottom: 0;
`;

const createBaselineStyles = (count) => {
  return [...Array(count).keys()].reduce((prev, current) => {
    return `${prev}
    .b-gantt.b-show-baseline-${
      current + 1
    } .b-task-baseline[data-index='${current}'] {
        display: block !important;
      }
    `;
  }, '');
};

export const BaselineStyles = createGlobalStyle`
  ${(props: { count: number }) => `
    .b-gantt-baseline-tooltip-wrapper {
      width: 240px;

      .b-gantt-task-title {
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
      .baseline-name-wrapper {
        margin-bottom: var(--spacing-04);
        display: flex;
        .baseline-name {
          font-size: var(--font-size-sm);
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
      }
      .baseline-data-table {
        list-style: none;
        padding: 0;
        margin: 0;
        li {
          display: flex;
          justify-content: space-between;
          margin-bottom: var(--spacing-02);
        }
        margin-bottom: var(--spacing-05) !important;
      }
      .baseline-status-message {
        margin-bottom: var(--spacing-02) !important;
        .statusicon {
          margin-right: var(--spacing-04) !important;
        }
      }
    }
    .b-max-baseline-selection {
      .b-gantt-task {
        height: 16px !important;
        min-height: 16px !important;
        max-height: 16px !important;
      }
    }
    ${createBaselineStyles(props.count)}
  `}
`;

export const BASELINE_SELECTION_MAX_COUNT = 3;

export const BaselineToolbar: FC<{
  selectedBaselines: number[];
  setBaseline: (index: number) => void;
  toggleBaseline: (id: number, index: number, value: boolean) => void;
  projectId: number;
  ganttRef: any;
}> = ({
  selectedBaselines: selection,
  setBaseline,
  toggleBaseline,
  projectId,
  ganttRef,
}) => {
  const translate = useTranslate();
  const [showBaselineDropdown, setShowBaselineDropDown] = useState(false);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editTarget, setEditTarget] = useState<ProjectBaseline | null>(null);

  const menuRef = useRef(null);

  const { data: baselines } = useProjectBaselineByProjectId(projectId);

  const activeBaselines = useActiveBaselinesByProjectId(projectId);
  const { getCreateRequestPayload } = useBaselineRequestPayload({ ganttRef });

  const { mutateAsync: createBaseline, isLoading: isCreating } =
    useCreateProjectBaselineMutation(projectId);
  const { mutateAsync: updateBaseline, isLoading: isEditing } =
    useUpdateProjectBaselineMutation(projectId);
  const { mutateAsync: deleteBaseline, isLoading: isDeleting } =
    useDeleteProjectBaselineMutation(projectId);

  const onAdd = useCallback(() => {
    setShowCreateModal(true);
  }, []);

  const onEdit = useCallback((event, baseline) => {
    event.preventDefault();
    setEditTarget(baseline);
  }, []);

  const hideCreateModal = useCallback(() => setShowCreateModal(false), []);
  const hideEditModal = useCallback(() => setEditTarget(null), []);

  const toggleBaselineOnAdd = useCallback(
    (id, index) => {
      if (selection.length >= BASELINE_SELECTION_MAX_COUNT) {
        const targetBaselineId = selection[0];
        const targetIndex = baselines.findIndex(
          (_baseline) => _baseline.baselineId === targetBaselineId,
        );

        toggleBaseline(targetBaselineId, targetIndex, false);
      }
      toggleBaseline(id, index, true);
    },
    [baselines, selection, toggleBaseline],
  );

  const onAddSubmit = useCallback(
    async (formValues: BaselineFormProps) => {
      const payload = getCreateRequestPayload(formValues);

      try {
        const newBaseline = await createBaseline(payload);

        toastService({
          title: translate('baseline.create.success.title'),
          message: translate('baseline.create.success.message', {
            name: newBaseline.baselineName,
          }),
        }).success();
        const newIndex = baselines.length;

        setBaseline(newIndex + 1);
        toggleBaselineOnAdd(newBaseline.baselineId, newIndex);
      } catch (e) {
        toastService({
          title: translate('baseline.create.error.title'),
          message: translate('baseline.create.error.message'),
        }).error();
      } finally {
        setShowCreateModal(false);
      }
    },
    [
      baselines.length,
      createBaseline,
      getCreateRequestPayload,
      setBaseline,
      toggleBaselineOnAdd,
      translate,
    ],
  );

  const onEditSubmit = useCallback(
    async (id: number, formValues: BaselineFormProps) => {
      const payload: ProjectBaselineUpdateRequest = {
        baselineName: formValues.name,
        baselineId: id,
        baselineNote: formValues.notes,
      };

      try {
        await updateBaseline(payload);
        toastService({
          title: translate('baseline.update.success.title'),
          message: translate('baseline.update.success.message', {
            name: formValues.name,
          }),
        }).success();
      } catch (e) {
        toastService({
          title: translate('baseline.update.error.title'),
          message: translate('baseline.update.error.message', {
            name: formValues.name,
          }),
        }).error();
      } finally {
        setEditTarget(null);
      }
    },
    [translate, updateBaseline],
  );

  const onDeleteConfirm = useCallback(
    async (baselineId: number) => {
      const baseline = baselines.find(
        (_baseline) => _baseline.baselineId === baselineId,
      );

      try {
        await deleteBaseline(baselineId);
        toastService({
          title: translate('baseline.delete.success.title'),
          message: translate('baseline.delete.success.message', {
            name: baseline?.baselineName,
          }),
        }).success();
        const _index = baselines.findIndex(
          (_baseline) => _baseline.baselineId === baselineId,
        );

        toggleBaseline(baselineId, _index, false);
      } catch (e) {
        toastService({
          title: translate('baseline.delete.error.title'),
          message: translate('baseline.delete.error.message', {
            name: baseline?.baselineName,
          }),
        }).error();
      } finally {
        setEditTarget(null);
      }
    },
    [baselines, deleteBaseline, toggleBaseline, translate],
  );

  const setBaselineMenu = useMemo(
    () => (
      <MenuWrapper ref={menuRef}>
        <BaselineMenu>
          <SetBaselineHeader>
            <div>
              <FormattedMessage id="baselines" />
            </div>
            <AddButton type="link" size="small" onClick={onAdd}>
              <Add16 />
              <FormattedMessage id="templates.placeholder.create.link" />
            </AddButton>
          </SetBaselineHeader>
          {activeBaselines.length <= 0 ? (
            <EmptyState>
              <FormattedMessage id="baseline.empty.state" />
            </EmptyState>
          ) : (
            baselines.map((_baseline, index) =>
              _baseline.deleted === true ? null : (
                <BaselineMenuItem key={_baseline.baselineId}>
                  <DisabledTooltip
                    placement="right"
                    title={
                      !selection.includes(_baseline.baselineId) &&
                      selection.length >= BASELINE_SELECTION_MAX_COUNT ? (
                        <FormattedMessage id="baseline.select.disabled" />
                      ) : (
                        ''
                      )
                    }
                  >
                    <StyledCheckbox
                      checked={selection.includes(_baseline.baselineId)}
                      disabled={
                        !selection.includes(_baseline.baselineId) &&
                        selection.length >= BASELINE_SELECTION_MAX_COUNT
                      }
                      onChange={(e) =>
                        toggleBaseline(
                          _baseline.baselineId,
                          index,
                          e.target.checked,
                        )
                      }
                    >
                      <BaselineName>
                        <Typography.Text
                          ellipsis={{
                            tooltip: (
                              <>
                                {_baseline.baselineName ? (
                                  _baseline.baselineName
                                ) : (
                                  <FormattedMessage id="baseline" />
                                )}
                              </>
                            ),
                          }}
                        >
                          {_baseline.baselineName ? (
                            _baseline.baselineName
                          ) : (
                            <FormattedMessage id="baseline" />
                          )}
                        </Typography.Text>
                        {_baseline.baselineNote && (
                          <Tooltip
                            title={
                              <TooltipNote>
                                {_baseline.baselineNote}
                              </TooltipNote>
                            }
                          >
                            <NoteIcon>
                              <NoteSvg />
                            </NoteIcon>
                          </Tooltip>
                        )}
                      </BaselineName>
                    </StyledCheckbox>
                    <Edit16
                      className="edit-icon"
                      onClick={(event) => onEdit(event, _baseline)}
                    />
                  </DisabledTooltip>
                </BaselineMenuItem>
              ),
            )
          )}
        </BaselineMenu>
      </MenuWrapper>
    ),
    [
      activeBaselines.length,
      baselines,
      onAdd,
      onEdit,
      selection,
      toggleBaseline,
    ],
  );

  const onShowBaselineDropdownVisibleChange = (value) => {
    setShowBaselineDropDown(value);
  };

  return (
    <>
      <Dropdown
        trigger={['click']}
        overlay={setBaselineMenu}
        visible={showBaselineDropdown}
        onVisibleChange={onShowBaselineDropdownVisibleChange}
      >
        <Button size="small" icon={<Menu16 />}>
          <FormattedMessage id="baseline" />
        </Button>
      </Dropdown>
      {showCreateModal && (
        <BaselineAddModal
          getContainer={menuRef.current}
          baselines={activeBaselines}
          onCancel={hideCreateModal}
          onSubmit={onAddSubmit}
          isSubmitting={isCreating}
        />
      )}
      {editTarget !== null && (
        <BaselineEditModal
          target={editTarget}
          baselines={activeBaselines}
          getContainer={menuRef.current}
          onCancel={hideEditModal}
          onSubmit={onEditSubmit}
          onDeleteConfirm={onDeleteConfirm}
          isEditing={isEditing}
          isDeleting={isDeleting}
        />
      )}
    </>
  );
};
